/**
 * Client-side scenario computation, mirroring
 * src/services/ProfitScenarioService.compute so slider changes render
 * live without a round trip. The server recomputes on save — the
 * server result is authoritative.
 */

import type { ScenarioDrivers, ScenarioResult } from '@/types/profitScenario';

const MONTHS_PER_QUARTER = 3;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Proportional labour scaling: moving the billable-people lever by
 * delta adds (or removes) delta x costPerPerson of monthly salary cost
 * from the current labour figure, floored at zero. Anchored to the
 * current labour value so manual salary edits are preserved as the base.
 */
/**
 * Proportional labour scaling: moving the billable-people lever by
 * delta adds (or removes) delta x costPerPerson of monthly salary cost
 * from the current labour figure, floored at zero. Anchored to the
 * current labour value so manual salary edits are preserved as the base.
 *
 * Fail-safe on invalid inputs (SEC-VAL-01, OWASP A03): if any argument
 * is non-finite (NaN/±Infinity — e.g. from a tampered API response or a
 * stale-state race), the function returns the current labour value
 * UNCHANGED rather than propagating the bad value into drivers. A
 * console.warn is emitted so the bug is observable without crashing the
 * React render (the caller does not try/catch).
 */
export function adjustLabourForHeadcount(
  currentLabourMonthly: number,
  headcountDelta: number,
  costPerBillablePersonMonthly: number
): number {
  if (
    !Number.isFinite(currentLabourMonthly) ||
    !Number.isFinite(headcountDelta) ||
    !Number.isFinite(costPerBillablePersonMonthly)
  ) {
    console.warn('[scenarioMath] adjustLabourForHeadcount got non-finite inputs', {
      currentLabourMonthly,
      headcountDelta,
      costPerBillablePersonMonthly,
    });
    return Number.isFinite(currentLabourMonthly) ? currentLabourMonthly : 0;
  }
  return Math.max(
    0,
    round2(currentLabourMonthly + headcountDelta * costPerBillablePersonMonthly)
  );
}

export function computeScenario(
  drivers: ScenarioDrivers,
  targetMarginPercent: number
): ScenarioResult {
  const billableHoursMonthly =
    drivers.billableHeadcount *
    drivers.capacityHoursPerPerson *
    (drivers.utilisationPercent / 100);
  const servicesRevenueMonthly =
    billableHoursMonthly * drivers.averageHourlyRate;
  const totalRevenueMonthly =
    servicesRevenueMonthly +
    drivers.productRevenueMonthly +
    drivers.otherRevenueMonthly;
  const totalCostsMonthly =
    drivers.labourMonthly + drivers.contractorMonthly +
    drivers.overheadsMonthly + drivers.aiMonthly;
  const npbtMonthly = totalRevenueMonthly - totalCostsMonthly;
  const npbtMarginPercent = totalRevenueMonthly > 0
    ? (npbtMonthly / totalRevenueMonthly) * 100
    : 0;

  return {
    billableHoursMonthly: round2(billableHoursMonthly),
    servicesRevenueMonthly: round2(servicesRevenueMonthly),
    totalRevenueMonthly: round2(totalRevenueMonthly),
    totalCostsMonthly: round2(totalCostsMonthly),
    npbtMonthly: round2(npbtMonthly),
    npbtMarginPercent: round2(npbtMarginPercent),
    totalRevenueQuarter: round2(totalRevenueMonthly * MONTHS_PER_QUARTER),
    totalCostsQuarter: round2(totalCostsMonthly * MONTHS_PER_QUARTER),
    npbtQuarter: round2(npbtMonthly * MONTHS_PER_QUARTER),
    meetsTarget: npbtMarginPercent >= targetMarginPercent,
  };
}
